import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';

import Segment from '../../../components/Segment';
import {
  Status as StatusInterface,
  GlanceParams,
  GlanceMarket,
  GlanceAdPlatform,
  DateRange,
  Segment as SegmentType,
  IdNameObject
} from '../../../types';
import { ReduxStates } from '../../../redux/reduxTypes';

import { endpoints } from './../config';
import {
  fetchGlanceSummaryData,
  generateSummaryMediaSegment,
  generateSummarySalesSegment,
  fetchSpgr
} from './../helpers';
import { useOktaAuth } from '@okta/okta-react';

interface Props {
  chosenMarketId: string;
  chosenAdPlatformId: string;
  chosenRetailerId: string;
  defaultMarket: GlanceMarket;
  defaultAdPlatform: GlanceAdPlatform;
  defaultRetailer: IdNameObject;
  dateRange: DateRange;
  setFetching: (fetching: boolean) => void;
  defaultParams: any;
}

const AmazonSummary = ({
  chosenMarketId,
  chosenAdPlatformId,
  defaultMarket,
  defaultAdPlatform,
  dateRange,
  setFetching,
  defaultParams,
  chosenRetailerId,
  defaultRetailer
}: Props): React.ReactElement => {
  const [glanceSummaryDataSales, setGlanceSummaryDataSales] = useState<SegmentType[] | null>(null);
  const [glanceSummaryDataMedia, setGlanceSummaryDataMedia] = useState<(SegmentType | null)[] | null>(null);
  const [spgrData, setSpgrData] = useState<SegmentType | null>(null);
  const [status, setStatus] = useState<StatusInterface[]>([]);
  const copyData = useSelector((state: ReduxStates) => state.copyData);
  const client = useSelector((state: ReduxStates) => state.client);
  const { oktaAuth } = useOktaAuth();

  const fetchGlanceSummarySales = async (params: GlanceParams, segment: any) => {
    try {
      const data = await fetchGlanceSummaryData(segment.endpoint, params, segment.title, oktaAuth);
      setGlanceSummaryDataSales(generateSummarySalesSegment(data));
    } catch (error: any) {
      setStatus([...status, { state: 'error', message: error }]);
    }
  };
  const fetchGlanceSummaryMedia = async (params: GlanceParams, segment: any) => {
    try {
      const data = await fetchGlanceSummaryData(segment.endpoint, params, segment.title);
      const { glanceViewSummary } = data;
      if (glanceViewSummary) {
        const isAmazon = chosenRetailerId === '1';
        const summaryMediaSegment = generateSummaryMediaSegment(glanceViewSummary, client, isAmazon);
        if (summaryMediaSegment && !isAmazon) {
          const filteredSegmentsData = summaryMediaSegment?.filter(
            (value) => value?.title !== 'Spend as % of Gross Revenue'
          );
          setGlanceSummaryDataMedia(filteredSegmentsData);
        } else {
          setGlanceSummaryDataMedia(summaryMediaSegment);
        }
      }
    } catch (error: any) {
      setStatus([...status, { state: 'error', message: error }]);
    }
  };
  const fetchSpgrData = async (params: any) => {
    try {
      const spgrData = await fetchSpgr(params);
      setSpgrData(spgrData.spgr);
    } catch (error: any) {
      setStatus([...status, { state: 'error', message: error }]);
    }
  };

  useEffect(() => {
    if (chosenMarketId && chosenAdPlatformId && defaultParams && chosenRetailerId) {
      //reset
      resetSummaryData();

      const params = {
        ...defaultParams,
        marketIds: chosenMarketId === defaultMarket.id ? '' : chosenMarketId,
        adPlatformIds: chosenAdPlatformId === defaultAdPlatform.id ? '' : chosenAdPlatformId,
        retailerIds: chosenRetailerId === defaultRetailer.id ? '' : chosenRetailerId,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate
      };
      const path = 'summary';
      const segments = endpoints[path].segments;

      setFetching(true);
      if (chosenRetailerId === '1') {
        fetchGlanceSummarySales(params, segments[0]);
        fetchSpgrData(params);
      }
      fetchGlanceSummaryMedia(params, segments[1]);
    }
  }, [chosenMarketId, defaultParams, chosenRetailerId]);

  const resetSummaryData = () => {
    setGlanceSummaryDataSales(null);
    setGlanceSummaryDataMedia(null);
    setSpgrData(null);
  };

  useEffect(() => {
    if ((glanceSummaryDataSales || chosenRetailerId !== '1') && glanceSummaryDataMedia) {
      setFetching(false);
    }
  }, [glanceSummaryDataSales, glanceSummaryDataMedia]);

  return (
    <React.Fragment>
      {(glanceSummaryDataSales || chosenRetailerId !== '1') && glanceSummaryDataMedia ? (
        <React.Fragment>
          {glanceSummaryDataSales &&
            glanceSummaryDataSales.map((segment: any, index: number) => {
              return (
                <Segment
                  key={`${index}-cs`}
                  variant={'glanceView'}
                  title={segment.title.toUpperCase()}
                  totalValue={segment.currentValue}
                  unit={segment.currentValueType === 'percentage' ? '%' : ''}
                  currency={segment.currencyCode}
                  WoW={segment.wowPercentage}
                  displayData={segment.displayData}
                  tooltipData={chosenMarketId === defaultMarket.id ? segment.dataStatus : null}
                />
              );
            })}
          {glanceSummaryDataMedia.map((segment: any, index: number) => {
            if (client === 'demo' && segment.title.toUpperCase() === 'SPEND AS % OF GROSS REVENUE') {
              segment.currentValue = 6.91;
              segment.wowPercentage = -1.39;
              segment.currentValueType = 'percentage';
            } else if (segment.title.toUpperCase() === 'SPEND AS % OF GROSS REVENUE') {
              segment.currentValue = spgrData?.currentValue;
              segment.wowPercentage = spgrData?.wowPercentage;
              segment.currentValueType = spgrData?.currentValueType;
              segment.currencyCode = spgrData?.currencyCode;
              segment.dataStatus = spgrData?.dataStatus;
            }

            return (
              <>
                {segment && (
                  <Segment
                    key={`${index}-cm`}
                    variant={'glanceView'}
                    title={segment.title.toUpperCase()}
                    totalValue={segment.currentValue}
                    unit={segment.currentValueType === 'percentage' ? '%' : ''}
                    currency={segment.currencyCode}
                    WoW={segment.wowPercentage}
                    displayData={segment.displayData}
                    tooltipData={chosenMarketId === defaultMarket.id ? segment.dataStatus : null}
                  />
                )}
              </>
            );
          })}
        </React.Fragment>
      ) : (
        <p className="m-0 font-nunitoRegular text-fontBlock text-xs mb-[15px]">{copyData?.loading}</p>
      )}
    </React.Fragment>
  );
};

export default AmazonSummary;
